import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import documentModel, { Document, DocumentState, ObjectType, RecordingAnnotation } from '@/core/models/document.model'
import _ from 'lodash'
import { RootState } from '@/redux/rootReducer'

export type DocumentReducerState = DocumentState

const initialState: DocumentReducerState = _.merge({}, documentModel.getInitialState(), {
  requestTracker: {
    addPrescription: { error: null, loading: false },
    sendChat: { error: null, loading: false },
    sendDocumentsByEmail: { error: null, loading: false },
    qrcode: { loading: false, error: null },
    dictaphone: { loading: false, error: null },
    updateRecordingAnnotation: { error: null, loading: false },
    deleteRecordingAnnotation: { error: null, loading: false },
    getFileByKey: { error: null, loading: false },
    getFileByObject: { error: null, loading: false },
    findFilesByCriteria: { error: null, loading: false },
  },
})

export const selectQrCodeValue = (state: RootState) => state.document.qrcode

export const selectDocumentCreateRequestTracker = (state: RootState) => state.document.requestTracker.create
export const selectDocumentUpdateRequestTracker = (state: RootState) => state.document.requestTracker.update
export const selectDocumentDeleteRequestTracker = (state: RootState) => state.document.requestTracker.delete
export const selectQrCodeRequestTracker = (state: RootState) => state.document.requestTracker.qrcode
export const selectSendDocumentsByEmailRequestTracker = (state: RootState) => state.document.requestTracker.sendDocumentsByEmail
export const selectDictaphoneRequestTracker = (state: RootState) => state.document.requestTracker.dictaphone
export const selectRecordingAnnotationDeleteRequestTracker = (state: RootState) => state.document.requestTracker.deleteRecordingAnnotation
export const selectRecordingAnnotationUpdateRequestTracker = (state: RootState) => state.document.requestTracker.updateRecordingAnnotation
export const selectAddPrescriptionRequestTracker = (state: RootState) => state.document.requestTracker.addPrescription
export const selectGetFileByKeyRequestTracker = (state: RootState) => state.document.requestTracker.getFileByKey
export const selectGetFileByObjectRequestTracker = (state: RootState) => state.document.requestTracker.getFileByObject
export const selectFindFilesByCriteriaRequestTracker = (state: RootState) => state.document.requestTracker.findFilesByCriteria

// Selectors for accessing documents
export const selectDocumentByKey = (state: RootState, key: string): Document | undefined => {
  // Look through all object types and IDs to find the document
  for (const objectType in state.document.documents) {
    for (const objectId in state.document.documents[objectType]) {
      if (state.document.documents[objectType][objectId][key]) {
        return state.document.documents[objectType][objectId][key]
      }
    }
  }
  return undefined
}

export const selectDocumentsByObjectType = (state: RootState, objectType: ObjectType, objectId: string): Document[] => {
  if (!state.document.documents[objectType]?.[objectId]) {
    return []
  }
  return Object.values(state.document.documents[objectType][objectId])
}

const documentSlice = createSlice({
  name: documentModel.reducerName,
  initialState: initialState,
  reducers: {
    addDocument(state, action: PayloadAction<{ documentFile: File; isRecording?: boolean; objectType: ObjectType; objectId: string }>) {
      state.requestTracker.create.loading = true
    },
    addDocumentSuccess(state, action: PayloadAction<{ document: Document; objectId: string }>) {
      const { document, objectId } = action.payload
      // Store the document in the documents structure
      if (!state.documents[document.objectType]) {
        state.documents[document.objectType] = {}
      }
      if (!state.documents[document.objectType][objectId]) {
        state.documents[document.objectType][objectId] = {}
      }
      state.documents[document.objectType][objectId][document.key] = document

      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    addDocumentFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },

    addDictaphoneDocument(state, action: PayloadAction<{ documentFile: File; objectType: ObjectType; objectId: string }>) {
      state.requestTracker.dictaphone.loading = true
    },
    addDictaphoneDocumentSuccess(state) {
      state.requestTracker.dictaphone.error = null
      state.requestTracker.dictaphone.loading = false
    },
    addDictaphoneDocumentFailure(state) {
      state.requestTracker.dictaphone.loading = false
    },
    addBiologicalDocument(state, action: PayloadAction<{ documentFile: File; objectType: ObjectType; objectId: string; biologicalId: string }>) {
      state.requestTracker.create.loading = true
    },
    addBiologicalDocumentSuccess(state, action: PayloadAction<{ document: Document; objectId: string }>) {
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    addBiologicalDocumentFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    deleteDocuments: {
      reducer: (state, action: PayloadAction<{ documents: Omit<Document, 'url'>[]; objectType: ObjectType; objectId: string }>) => {
        state.requestTracker.delete.loading = true
      },
      prepare: (documents: Omit<Document, 'url'>[], objectType: ObjectType, objectId: string) => ({
        payload: {
          documents,
          objectType,
          objectId,
        },
      }),
    },
    deleteDocumentsSuccess(state) {
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteDocumentsFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    generateRapport: {
      reducer: (state, action: PayloadAction<{ documents: Omit<Document, 'url'>[]; objectType: ObjectType; objectId: string }>) => {
        state.requestTracker.delete.loading = true
      },
      prepare: (documents: Omit<Document, 'url'>[], objectType: ObjectType, objectId: string) => ({
        payload: {
          documents,
          objectType,
          objectId,
        },
      }),
    },
    generateRapportSuccess(state) {
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    generateRapportFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    generateQrCode(state, action: PayloadAction<{ url: string }>) {
      state.requestTracker.qrcode.loading = true
    },
    generateQrCodeSuccess(state, action: PayloadAction<{ url: string }>) {
      state.requestTracker.qrcode.error = null
      state.requestTracker.qrcode.loading = false
      state.qrcode = action.payload.url
    },
    generateQrCodeFailure(state, action) {
      state.requestTracker.qrcode.error = action.payload
      state.requestTracker.qrcode.loading = false
    },
    updateFilename: {
      reducer(state, action: PayloadAction<{ file: Omit<Document, 'url'>; filename: string; objectType: ObjectType; objectId: string }>) {
        state.requestTracker.update.loading = true
      },
      prepare: (file: Omit<Document, 'url'>, filename: string, objectType: ObjectType, objectId: string) => ({
        payload: { file, filename, objectType, objectId },
      }),
    },
    updateFilenameSucesss(state) {
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateFilenameFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    sendDocumentsByEmail(state, action: PayloadAction<{ objectType: ObjectType; objectId: string; emails: string[]; filenames: string[]; pdfPassword?: string }>) {
      state.requestTracker.sendDocumentsByEmail.loading = true
    },
    sendDocumentsByEmailSucess(state) {
      state.requestTracker.sendDocumentsByEmail.error = null
      state.requestTracker.sendDocumentsByEmail.loading = false
    },
    sendDocumentsByEmailFailure(state, action) {
      state.requestTracker.sendDocumentsByEmail.error = action.payload
      state.requestTracker.sendDocumentsByEmail.loading = false
    },
    updateRecordingAnnotation: {
      reducer(state, action: PayloadAction<{ key: string; annotation: RecordingAnnotation; objectType: ObjectType; objectId: string }>) {
        state.requestTracker.updateRecordingAnnotation.loading = true
      },
      prepare: (key: string, annotation: RecordingAnnotation, objectType: ObjectType, objectId: string) => ({
        payload: { key, annotation, objectType, objectId },
      }),
    },
    updateRecordingAnnotationSuccess(state) {
      state.requestTracker.updateRecordingAnnotation.error = null
      state.requestTracker.updateRecordingAnnotation.loading = false
    },
    updateRecordingAnnotationFailure(state, action) {
      state.requestTracker.updateRecordingAnnotation.error = action.payload
      state.requestTracker.updateRecordingAnnotation.loading = false
    },
    deleteRecordingAnnotation: {
      reducer(state, action: PayloadAction<{ fileKey: string; annotationId: string; objectType: ObjectType; objectId: string }>) {
        state.requestTracker.deleteRecordingAnnotation.loading = true
      },
      prepare: (fileKey: string, annotationId: string, objectType: ObjectType, objectId: string) => ({
        payload: { fileKey, annotationId, objectType, objectId },
      }),
    },
    deleteRecordingAnnotationSuccess(state) {
      state.requestTracker.deleteRecordingAnnotation.error = null
      state.requestTracker.deleteRecordingAnnotation.loading = false
    },
    deleteRecordingAnnotationFailure(state, action) {
      state.requestTracker.deleteRecordingAnnotation.error = action.payload
      state.requestTracker.deleteRecordingAnnotation.loading = false
    },
    addPrescription(state, action: PayloadAction<{ consultationId: string; prescriptionId: string }>) {
      state.requestTracker.addPrescription.error = null
      state.requestTracker.addPrescription.loading = true
    },
    addPrescriptionSuccess(state) {
      state.requestTracker.addPrescription.loading = false
    },
    addPrescriptionFailure(state, action) {
      state.requestTracker.addPrescription.error = action.payload
      state.requestTracker.addPrescription.loading = false
    },

    // New actions for retrieving files with signed URLs
    getFileByKey(state, action: PayloadAction<{ key: string }>) {
      state.requestTracker.getFileByKey.loading = true
      state.requestTracker.getFileByKey.error = null
    },
    getFileByKeySuccess(state, action: PayloadAction<Document>) {
      const document = action.payload

      // Store the document in the documents structure
      if (!state.documents[document.objectType]) {
        state.documents[document.objectType] = {}
      }

      // If we don't know the specific objectId, use the document key as temp objectId
      const objectId = document.key.split('/')[1] || document.key

      if (!state.documents[document.objectType][objectId]) {
        state.documents[document.objectType][objectId] = {}
      }

      state.documents[document.objectType][objectId][document.key] = document

      state.requestTracker.getFileByKey.loading = false
      state.requestTracker.getFileByKey.error = null
    },
    getFileByKeyFailure(state, action) {
      state.requestTracker.getFileByKey.loading = false
      state.requestTracker.getFileByKey.error = action.payload
    },

    getFileByObject(state, action: PayloadAction<{ objectType: ObjectType; objectId: string; filename: string }>) {
      state.requestTracker.getFileByObject.loading = true
      state.requestTracker.getFileByObject.error = null
    },
    getFileByObjectSuccess(state, action: PayloadAction<Document>) {
      const document = action.payload

      // Store the document in the documents structure
      if (!state.documents[document.objectType]) {
        state.documents[document.objectType] = {}
      }

      // Extract objectId from the document's key if not directly available
      const objectId = document.key.split('/')[1] || document.key

      if (!state.documents[document.objectType][objectId]) {
        state.documents[document.objectType][objectId] = {}
      }

      state.documents[document.objectType][objectId][document.key] = document

      state.requestTracker.getFileByObject.loading = false
      state.requestTracker.getFileByObject.error = null
    },
    getFileByObjectFailure(state, action) {
      state.requestTracker.getFileByObject.loading = false
      state.requestTracker.getFileByObject.error = action.payload
    },

    findFilesByCriteria(state, action: PayloadAction<{ objectType: ObjectType; objectId: string; filenamePattern?: string }>) {
      state.requestTracker.findFilesByCriteria.loading = true
      state.requestTracker.findFilesByCriteria.error = null
    },
    findFilesByCriteriaSuccess(state, action: PayloadAction<Document[]>) {
      const documents = action.payload

      // Group documents by object type and ID
      documents.forEach((document) => {
        if (!state.documents[document.objectType]) {
          state.documents[document.objectType] = {}
        }

        // Extract objectId from the document's key if not directly available
        const objectId = document.key.split('/')[1] || document.key

        if (!state.documents[document.objectType][objectId]) {
          state.documents[document.objectType][objectId] = {}
        }

        state.documents[document.objectType][objectId][document.key] = document
      })

      state.requestTracker.findFilesByCriteria.loading = false
      state.requestTracker.findFilesByCriteria.error = null
    },
    findFilesByCriteriaFailure(state, action) {
      state.requestTracker.findFilesByCriteria.loading = false
      state.requestTracker.findFilesByCriteria.error = action.payload
    },
  },
})

export const {
  addDocument,
  addDocumentSuccess,
  addDocumentFailure,
  addDictaphoneDocument,
  addDictaphoneDocumentSuccess,
  addDictaphoneDocumentFailure,
  deleteDocuments,
  deleteDocumentsSuccess,
  deleteDocumentsFailure,
  generateRapport,
  generateRapportSuccess,
  generateRapportFailure,
  generateQrCode,
  generateQrCodeSuccess,
  generateQrCodeFailure,
  updateFilename,
  updateFilenameSucesss,
  updateFilenameFailure,
  sendDocumentsByEmail,
  sendDocumentsByEmailSucess,
  sendDocumentsByEmailFailure,
  addBiologicalDocument,
  updateRecordingAnnotation,
  updateRecordingAnnotationSuccess,
  updateRecordingAnnotationFailure,
  deleteRecordingAnnotation,
  deleteRecordingAnnotationSuccess,
  deleteRecordingAnnotationFailure,
  addPrescription,
  addPrescriptionSuccess,
  addPrescriptionFailure,
  getFileByKey,
  getFileByKeySuccess,
  getFileByKeyFailure,
  getFileByObject,
  getFileByObjectSuccess,
  getFileByObjectFailure,
  findFilesByCriteria,
  findFilesByCriteriaSuccess,
  findFilesByCriteriaFailure,
} = documentSlice.actions

export default documentSlice.reducer
